import styled from "styled-components";
import { ButtonComp, SelectCompWithFormattedLabel, ModalCompAdvanceUpdate } from "../../../../components";
import { useGetMembersQuery } from "../../../../store/admin/memberManagement/memberSlice";
import { useGetStaffListQuery } from "../../../../store/admin/staff";
import { useViewMemberQuery } from "../../../../store/admin/memberManagement/memberSlice";
import { useGetSingleStaffQuery } from "../../../../store/admin/staff";
import { toast } from "react-hot-toast";
import { useMemo, useState } from "react";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { CardComp } from "../../../../components";

const AddGroupMemberModal = ({
  onBack,
  onProceed,
  submitText = "Add to group",
  title = "Add member or staff",
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [userType, setUserType] = useState("member"); // "member" or "staff"
  const [viewProfileId, setViewProfileId] = useState(null);
  const [viewProfileType, setViewProfileType] = useState(null); // "member" or "staff"
  const [pageNumber] = useState(1);
  
  // Fetch members
  const { data: membersData = {}, error: membersError } = useGetMembersQuery(pageNumber);
  
  // Fetch staff
  const { data: staffData = {}, error: staffError } = useGetStaffListQuery(pageNumber);
  
  // Fetch member details for profile view
  const { data: memberDetails, isLoading: isLoadingMemberDetails } = useViewMemberQuery(
    viewProfileId,
    { skip: !viewProfileId || viewProfileType !== "member" }
  );
  
  // Fetch staff details for profile view
  const { data: staffDetails, isLoading: isLoadingStaffDetails } = useGetSingleStaffQuery(
    viewProfileId,
    { skip: !viewProfileId || viewProfileType !== "staff" }
  );
  
  const { members } = useMemo(() => {
    const { members: items = [] } = membersData;
    const formattedMembers = items
      .filter((item) => item && item.id)
      .map((member) => ({
        label: `${member?.first_name || ""} ${member?.last_name || ""}`.trim(),
        name: `${member?.first_name || ""} ${member?.last_name || ""}`.trim(),
        value: member?.id,
        photo: member?.photo,
        userId: member?.member_no || member?.member?.member_no || null,
        email: member?.email,
        phone: member?.phone,
        branch: member?.branch?.name,
      }));
    return { members: formattedMembers };
  }, [membersData]);

  const { staffs } = useMemo(() => {
    const { staffs: items = [] } = staffData;
    const formattedStaffs = items
      .filter((item) => item && item.id)
      .map((staff) => ({
        label: `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim(),
        name: `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim(),
        value: staff?.id,
        photo: staff?.photo,
        userId: staff?.staff_no || staff?.staff_id || null,
        email: staff?.email,
        phone: staff?.phone,
        branch: staff?.branch?.name,
      }));
    return { staffs: formattedStaffs };
  }, [staffData]);

  const currentOptions = userType === "member" ? members : staffs;
  const currentError = userType === "member" ? membersError : staffError;

  if (currentError) {
    toast.error(currentError?.data?.message || "An error occurred");
  }

  const handleProceed = () => {
    if (!selectedItems || selectedItems.length === 0) {
      toast.error(`Please select at least one ${userType}`);
      return;
    }
    // Format the selected items to match the expected structure
    // Use member_id for members and staff_id for staff to avoid ID collision
    const formattedItems = selectedItems.map((item) => {
      const baseItem = {
        label: item.label || item.name,
        user_type: userType, // Include user_type for backend to distinguish
      };
      
      // Use the correct field based on userType to avoid ID collision
      if (userType === "staff") {
        baseItem.staff_id = item.value;
      } else {
        baseItem.member_id = item.value;
      }
      
      return baseItem;
    });
    onProceed(formattedItems, userType);
  };

  // Custom filter function to search by name or ID number
  const filterOption = (option, searchText) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    const label = (option.label || option.name || "").toLowerCase();
    const userId = (option.userId || "").toString().toLowerCase();
    return label.includes(search) || userId.includes(search);
  };

  const handleViewProfile = (item) => {
    setViewProfileId(item.value);
    setViewProfileType(userType);
  };

  const handleCloseProfile = () => {
    setViewProfileId(null);
    setViewProfileType(null);
  };

  // Format option label with view profile button
  const formatOptionLabel = ({ photo, name, userId, value, label, ...props }) => {
    const displayId = userId || "N/A";
    const displayName = name || label || "N/A";
    return (
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="d-flex align-items-center flex-grow-1">
          {photo && (
            <FallbackImage
              className="rounded-circle me-3"
              width="24px"
              height="24px"
              src={photo}
            />
          )}
          <span>{`${displayName} | Membership No: ${displayId}`}</span>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-link text-primary p-0 ms-2"
          onClick={(e) => {
            e.stopPropagation();
            handleViewProfile({ value, label: displayName, ...props });
          }}
          style={{ fontSize: "12px", textDecoration: "none" }}
        >
          View Profile
        </button>
      </div>
    );
  };

  const modalItems = [
    {
      name: "View Profile",
      component: (
        <ProfileModal
          onClose={handleCloseProfile}
          userType={viewProfileType}
          memberData={viewProfileType === "member" ? memberDetails : null}
          staffData={viewProfileType === "staff" ? staffDetails : null}
          isLoading={viewProfileType === "member" ? isLoadingMemberDetails : isLoadingStaffDetails}
        />
      ),
    },
  ];

  return (
    <>
      <Wrapper>
        <div className="mb-4">
          <img
            src="/Icon/cancel.svg"
            role="button"
            onClick={onBack}
            alt="close"
            className="close-img"
          />
          <h5 className="fw-bold text-center">{title}</h5>
        </div>
        
        {/* User Type Toggle */}
        <div className="px-2 mb-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn flex-grow-1 ${userType === "member" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setUserType("member");
                setSelectedItems([]);
              }}
              style={{
                backgroundColor: userType === "member" ? "var(--color-primary)" : "transparent",
                borderColor: "var(--color-primary)",
                color: userType === "member" ? "#fff" : "var(--color-primary)",
                borderRadius: "10px",
                padding: "8px 16px",
              }}
            >
              Members
            </button>
            <button
              type="button"
              className={`btn flex-grow-1 ${userType === "staff" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setUserType("staff");
                setSelectedItems([]);
              }}
              style={{
                backgroundColor: userType === "staff" ? "var(--color-primary)" : "transparent",
                borderColor: "var(--color-primary)",
                color: userType === "staff" ? "#fff" : "var(--color-primary)",
                borderRadius: "10px",
                padding: "8px 16px",
              }}
            >
              Staff
            </button>
          </div>
        </div>

        <div className="py-2"></div>
        <div className="px-2">
          <label className="form-label mb-2 fw-semibold" style={{ fontSize: "14px", color: "#383838" }}>
            Select {userType === "member" ? "Member(s)" : "Staff"}
          </label>
          <SelectCompWithFormattedLabel
            arrayComp={currentOptions}
            selectedOption={selectedItems}
            setOption={setSelectedItems}
            placeholder={`Search by name or ${userType === "member" ? "member" : "staff"} number...`}
            filterOption={filterOption}
            isMulti={true}
            formatOptionLabel={formatOptionLabel}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 1070 }),
              control: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: "#fafafa",
                border: "1px solid #a1a0a0",
                borderRadius: "5px",
                minHeight: "48px",
                "&:hover": {
                  border: "1px solid var(--color-primary)",
                },
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#a1a0a0",
              }),
            }}
          />
          {selectedItems.length > 0 && (
            <small className="text-muted mt-2 d-block">
              {selectedItems.length} {selectedItems.length === 1 ? userType : `${userType}s`} selected
            </small>
          )}
        </div>
        <div className="row justify-content-between mt-4">
          <div className="col-5">
            <ButtonComp
              btnText={"Cancel"}
              ButtonInlineStyle={{ borderRadius: "10px" }}
              btnClassName={" text-1 btn w-100 py-2"}
              BorderColor={"var(--color-primary)"}
              onClick={() => onBack()}
            />
          </div>
          <div className="col-6">
            <ButtonComp
              btnText={submitText}
              ButtonInlineStyle={{
                backgroundColor: "var(--color-primary)",
                borderRadius: "10px",
              }}
              btnClassName={"border-0 text-white w-100 py-2"}
              onClick={handleProceed}
              disabled={!selectedItems || selectedItems.length === 0}
            />
          </div>
        </div>
      </Wrapper>
      
      <ModalCompAdvanceUpdate
        pageName={viewProfileId ? "View Profile" : null}
        arrayComp={modalItems}
        centered={true}
        size="lg"
      />
    </>
  );
};

// Profile Modal Component
const ProfileModal = ({ onClose, userType, memberData, staffData, isLoading }) => {
  const userData = userType === "member" ? memberData : staffData;
  
  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="p-4">
        <p>No data available</p>
        <ButtonComp
          btnText="Close"
          onClick={onClose}
          btnClassName="btn btn-secondary"
        />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0">
          {userType === "member" ? "Member" : "Staff"} Profile
        </h5>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={onClose}
          alt="close"
          style={{ cursor: "pointer" }}
        />
      </div>
      
      <CardComp cardClassName="border-0 shadow-sm">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <FallbackImage
                  src={userData?.photo || "/Images/TopHeader/avatar.svg"}
                  height="80"
                  width="80"
                  className="rounded-circle border border-2"
                  style={{ borderColor: "var(--color-primary)" }}
                  alt="Photo"
                />
              </div>
              <div>
                <h5 className="mb-1 text-dark fw-bold">
                  {userData?.first_name || ""} {userData?.last_name || ""}
                </h5>
                <p className="mb-1 text-muted small">
                  <i className="fas fa-id-card me-1"></i>
                  {userType === "member"
                    ? `Membership Number: ${userData?.member_no || "N/A"}`
                    : `Staff ID: ${userData?.staff_no || userData?.id || "N/A"}`}
                </p>
                <p className="mb-0 text-muted small">
                  <i className="fas fa-building me-1"></i>
                  {userData?.branch?.name || "Main Branch"}
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-2">
              <small className="text-muted d-block">Email</small>
              <span>{userData?.email || "N/A"}</span>
            </div>
            <div className="mb-2">
              <small className="text-muted d-block">Phone</small>
              <span>{userData?.phone || "N/A"}</span>
            </div>
            {userData?.address && (
              <div className="mb-2">
                <small className="text-muted d-block">Address</small>
                <span>{userData?.address}</span>
              </div>
            )}
          </div>
        </div>
      </CardComp>
      
      <div className="mt-3 text-end">
        <ButtonComp
          btnText="Close"
          onClick={onClose}
          btnClassName="btn btn-secondary"
          ButtonInlineStyle={{ borderRadius: "10px" }}
        />
      </div>
    </div>
  );
};

const Wrapper = styled.div`
  padding: 2rem;
  max-width: 600px;
  position: relative;
  
  .close-img {
    position: absolute;
    left: 8px;
    cursor: pointer;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
    }
  }
`;

export default AddGroupMemberModal;
